import { getUserData } from "../hooks/getUserRole";
import { useEffect, useState } from "react";
import { Button, Form, Col, Row, Tooltip } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import useGet from "../hooks/useGet";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import CommonButton from "./CommonButton";
import CustomInput from "./inputWidgets/CustomInput";
import usePut from "../hooks/usePut";
import { CloseOutlined, CheckCircleOutlined } from "@ant-design/icons";
import CustomSwitch from "./inputWidgets/CustomSwitch";
import ProfileIcon from "./../../assets/images/vectors/ProfileIcon";
import { normalizeEmailValue } from "../utils/normalizeFormEmails";

export const ProfileForm = ({ setIsModalOpen }) => {
  const [form] = Form.useForm();
  const loggedInUserData = getUserData();
  const id = loggedInUserData.account_no;
  const [qbConnectLoading, setQbConnectLoading] = useState(false);
  const showQuickBooksConnect =
    [4, 5].includes(loggedInUserData?.role_value) &&
    Number(loggedInUserData?.oem_license) === 1;

  const url = `${process.env.REACT_APP_API_URL}/api/accounts/profile/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/accounts/profile/`;

  const { data, loading } = useGet(url);
  const quickBooksConnected = Boolean(data?.data?.QuickBooksConnected);
  const quickBooksExpiredAt = data?.data?.QuickBooksExpiredAt
    ? dayjs(data.data.QuickBooksExpiredAt).format("YYYY-MM-DD HH:mm")
    : "";
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);

  const onFinish = (values) => {
    try {
      const {
        confirmPassword,
        OemLabel,
        DealerLabel,
        CustomerLabel,
        ...payload
      } = values;
      putAction({
        ...payload,
        Email: normalizeEmailValue(payload.Email),
      });
      // setIsModalOpen(false);
    } catch (error) {
      openErrorNotification("Something went wrong");
      console.log(error);
    }
  };

  //toast for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      setIsModalOpen(false);
    }
  }, [putData, putLoading, putError]);

  const onReset = () => {
    setIsModalOpen(false);
  };

  const handleQuickBooksConnect = async () => {
    if (quickBooksConnected || qbConnectLoading) return;
    try {
      setQbConnectLoading(true);
      const path = String(window.location.pathname || "/")
        .replace(/^\/+/, "")
        .replace(/\/+$/, "");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payments/quickbooks/connect/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedInUserData?.access_token}`,
          },
          params: {
            path: path || "site",
            profile: true,
          },
        },
      );
      const authUrl = response?.data?.data?.auth_url;
      if (!authUrl) {
        openErrorNotification(
          response?.data?.message || "QuickBooks authorization URL missing.",
        );
        setQbConnectLoading(false);
        return;
      }
      // Keep loading until the browser leaves this page for Intuit.
      window.location.href = authUrl;
    } catch (error) {
      setQbConnectLoading(false);
      openErrorNotification(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to connect QuickBooks.",
      );
    }
  };

  useEffect(() => {
    if (!id || loading) return;
    const getAccountInfoById = () => {
      try {
        if (data?.data) {
          form.setFieldsValue({
            AccountNo: String(data?.data?.AccountNo),
            UserId: String(data?.data?.UserId),
            AccountName: String(data.data.AccountName),
            TwoFactor: data.data.TwoFactor ? 1 : 0,
            SsoEnable: data.data.SsoEnable ? 1 : 0,
            AccountDesc: data.data.AccountDesc
              ? String(data.data.AccountDesc)
              : "",
            Email: data.data.Email ? String(data.data.Email) : "",
            OemLabel: data.data.OemLabel ? String(data.data.OemLabel) : "",
            DealerLabel: data.data.DealerLabel
              ? String(data.data.DealerLabel)
              : "",
            CustomerLabel: data.data.CustomerLabel
              ? String(data.data.CustomerLabel)
              : "",
          });
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        if (!loading) {
          console.log({ error });
          openErrorNotification("Error fetching data!");
        }
      }
    };

    getAccountInfoById();
  }, [id, data, loading, form]);

  useEffect(() => {
    if (putError) {
      const message =
        putError?.response?.data?.message || "Something went wrong";

      // Check if the error is email-related and set it on the field
      if (message.toLowerCase().includes("email")) {
        form.setFields([
          {
            name: "Email",
            errors: [message],
          },
        ]);
      } else {
        openErrorNotification(message);
      }
    } else if (!putLoading && !putError && putData) {
      // openConfirmNotification("Success");
      setIsModalOpen(false);
    }
  }, [putData, putLoading, putError]);

  const buttonsData = [
    // {
    //   label: "List",
    //   icon: <UnorderedListOutlined />,
    //   onClick: () => {
    //     navigate("/accountlist");
    //   },
    // },
    {
      label: "Apply",
      icon: <CheckCircleOutlined />,
      onClick: () => form.submit(),
    },
    {
      label: "Cancel",
      icon: <CloseOutlined />,
      onClick: onReset,
    },
  ];

  return (
    <Form
      validateTrigger={"onChange"}
      form={form}
      onFinish={onFinish}
      scrollToFirstError
      layout={"vertical"}
      autoComplete={"off"}
    >
      <div className=" bg-white rounded-[4px] overflow-hidden">
        <div className="bg-gray-200 py-[8px] px-[3px] rounded-sm flex items-center justify-between mb-2 p-2">
          <div className="flex items-center">
            <ProfileIcon />
            <h2 className="text-[14px] font-[Poppins] text-black ml-2">
              Profile
            </h2>
          </div>
          {showQuickBooksConnect ? (
            quickBooksConnected ? (
              <Tooltip
                title={
                  quickBooksExpiredAt
                    ? `Expires ${quickBooksExpiredAt}`
                    : undefined
                }
              >
                <Button
                  htmlType="button"
                  type="primary"
                  className="qb-connected-btn h-[30px] font-medium mr-1"
                  disabled
                >
                  QuickBooks Connected
                </Button>
              </Tooltip>
            ) : (
              <Button
                htmlType="button"
                type="primary"
                className="qb-connect-btn h-[30px] font-medium mr-1"
                loading={qbConnectLoading || loading}
                disabled={loading}
                onClick={handleQuickBooksConnect}
              >
                Connect QuickBooks
              </Button>
            )
          ) : null}
        </div>

        <div className="mt-4 px-2 ">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <CustomInput label="Username" name="UserId" loading={loading} />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Old Password"
                name="OldPassword"
                autoComplete="new-password"
                type="password"
                loading={loading}
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="New Password"
                name="password"
                autoComplete="new-password"
                type="password"
                loading={loading}
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Confirm Password"
                name="confirmPassword"
                autoComplete="new-password"
                type="password"
                loading={loading}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("password")) {
                        if (!value) {
                          return Promise.reject(
                            new Error("Confirm Password is required"),
                          );
                        }
                        if (getFieldValue("password") !== value) {
                          return Promise.reject(
                            new Error("Passwords do not match!"),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Description"
                name="AccountDesc"
                loading={loading}
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Email"
                name="Email"
                required={true}
                loading={loading}
              />
            </Col>
            {data?.data?.OemLabel ? (
              <Col span={24}>
                <CustomInput
                  label="OEM"
                  name="OemLabel"
                  disable
                  loading={loading}
                />
              </Col>
            ) : null}
            {data?.data?.DealerLabel ? (
              <Col span={24}>
                <CustomInput
                  label="Dealer"
                  name="DealerLabel"
                  disable
                  loading={loading}
                />
              </Col>
            ) : null}
            {data?.data?.CustomerLabel ? (
              <Col span={24}>
                <CustomInput
                  label="Customer"
                  name="CustomerLabel"
                  disable
                  loading={loading}
                />
              </Col>
            ) : null}
            <Col span={12}>
              <CustomSwitch
                label="Two Factor Authentication"
                name="TwoFactor"
                defaultChecked={0}
                loading={loading}
              />
            </Col>
            <Col span={12}>
              <CustomSwitch
                label="Single Sign-On "
                name="SsoEnable"
                defaultChecked={0}
                loading={loading}
              />
            </Col>
          </Row>
        </div>

        <div className="flex justify-center items-center ">
          <CommonButton buttonsData={buttonsData} />
        </div>
      </div>
    </Form>
  );
};
